/**
 * Connectors (task 48): read-only links to the systems a business runs, read with its consent. The first
 * is Microsoft 365, through Microsoft Graph. A connector turns what it reads into facts, and facts into
 * control readings, which staff can accept as answers with `API_DISCOVERED` provenance, as for outside-in
 * readings.
 */
export const CONNECTOR_KINDS = ['microsoft365'];
export const CONNECTOR_NAMES = { microsoft365: 'Microsoft 365' };
/** The Graph permissions (application) the Microsoft 365 connector asks for. All read-only. */
export const M365_PERMISSIONS = ['Policy.Read.All', 'AuditLog.Read.All', 'UserAuthenticationMethod.Read.All']; // prettier-ignore
const PHISHING_RESISTANT = new Set(['fido2', 'passKeyDeviceBound', 'passKeyDeviceBoundAuthenticator', 'windowsHelloForBusiness']); // prettier-ignore
const LEGACY_CLIENTS = new Set(['exchangeActiveSync', 'other']);
const requiresMfa = (p) => !!p.grantControls && ((p.grantControls.builtInControls ?? []).includes('mfa') || !!p.grantControls.authenticationStrength); // prettier-ignore
const stateOf = (p) => p.state === 'enabled'
    ? 'enforced'
    : p.state === 'enabledForReportingButNotEnforced'
        ? 'report_only'
        : 'none';
const strongest = (xs) => xs.includes('enforced') ? 'enforced' : xs.includes('report_only') ? 'report_only' : 'none';
export function m365Facts(raw) {
    const policies = raw.policies;
    const forAll = policies?.filter((p) => requiresMfa(p) && (p.conditions?.users?.includeUsers ?? []).includes('All')); // prettier-ignore
    const forAdmins = policies?.filter((p) => requiresMfa(p) && ((p.conditions?.users?.includeRoles ?? []).length > 0 || (p.conditions?.users?.includeUsers ?? []).includes('All'))); // prettier-ignore
    const blocksLegacy = policies?.some((p) => p.state === 'enabled' && (p.grantControls?.builtInControls ?? []).includes('block') && (p.conditions?.clientAppTypes ?? []).some((c) => LEGACY_CLIENTS.has(c)));
    const members = raw.registrations?.filter((r) => r.userType !== 'guest');
    const admins = members?.filter((r) => r.isAdmin);
    const sd = raw.securityDefaults?.isEnabled ?? null;
    return {
        security_defaults: sd,
        mfa_all_users: forAll ? strongest(forAll.map(stateOf)) : null,
        mfa_admins: forAdmins ? strongest(forAdmins.map(stateOf)) : null,
        legacy_auth_blocked: sd === true ? true : (blocksLegacy ?? null),
        users: members?.length ?? null,
        users_mfa_registered: members?.filter((r) => r.isMfaRegistered).length ?? null,
        admins: admins?.length ?? null,
        admins_mfa_registered: admins?.filter((r) => r.isMfaRegistered).length ?? null,
        admins_phishing_resistant: admins?.filter((r) => (r.methodsRegistered ?? []).some((m) => PHISHING_RESISTANT.has(m))).length ?? null, // prettier-ignore
    };
}
const share = (n, of) => (n === null || !of ? null : n / of);
const pct = (x) => `${Math.round(x * 100)}%`;
/** The controls Microsoft 365 settings show, as readings. A reading is left out when the facts are missing. */
export function m365Readings(f) {
    const out = [];
    const enforcedAll = f.security_defaults === true || f.mfa_all_users === 'enforced';
    const knownAll = f.security_defaults !== null || f.mfa_all_users !== null;
    const users = share(f.users_mfa_registered, f.users);
    if (knownAll && users !== null) {
        const how = f.security_defaults ? 'Security defaults on' : f.mfa_all_users === 'enforced' ? 'A Conditional Access policy requires MFA for everyone' : f.mfa_all_users === 'report_only' ? 'The MFA policy for everyone is only reporting, not enforced' : 'Nothing requires MFA for everyone'; // prettier-ignore
        const implementation = enforcedAll && users >= 0.95 ? 1 : enforcedAll || f.mfa_all_users === 'report_only' || users >= 0.5 ? 0.5 : 0; // prettier-ignore
        out.push({ control: 'IA-02', implementation, reason: `${how}; ${pct(users)} of ${f.users} users registered for MFA` }); // prettier-ignore
    }
    const admins = share(f.admins_mfa_registered, f.admins);
    if ((knownAll || f.mfa_admins !== null) && admins !== null) {
        const enforced = enforcedAll || f.mfa_admins === 'enforced';
        const implementation = enforced && admins === 1 ? 1 : enforced || admins === 1 ? 0.5 : 0;
        out.push({ control: 'IA-01', implementation, reason: `MFA ${enforced ? 'required' : 'not required'} for admins; ${f.admins_mfa_registered} of ${f.admins} admins registered` }); // prettier-ignore
    }
    if (f.legacy_auth_blocked !== null)
        out.push({ control: 'IA-07', implementation: f.legacy_auth_blocked ? 1 : 0, reason: f.legacy_auth_blocked ? (f.security_defaults ? 'Security defaults block legacy sign-in' : 'A Conditional Access policy blocks legacy sign-in') : 'Nothing blocks legacy sign-in' }); // prettier-ignore
    if (f.admins && f.admins_phishing_resistant !== null) {
        const n = f.admins_phishing_resistant;
        out.push({ control: 'IA-04', implementation: n === f.admins ? 1 : n > 0 ? 0.5 : 0, reason: `${n} of ${f.admins} admins have a passkey, security key or Windows Hello` }); // prettier-ignore
    }
    return out;
}
//# sourceMappingURL=connectors.js.map